/**
 * Meal plan generator — extracted from Orosz Coaching's tested etrendgenerator-7napos.html
 * Every function is pure and side-effect free. The frontend admin editor and the
 * portal view import from here.
 *
 * The RECIPES + INGREDIENTS databases live in /src/data (imported once).
 * See Fejlesztoi_atado_etrendgenerator.md for the algorithmic contract.
 */
import RECIPES from '../data/recipes.json';
import INGREDIENTS from '../data/ingredients.json';

export { RECIPES, INGREDIENTS };

// ============================== helpers =====================================
export const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const veletlen = a => a[Math.floor(Math.random() * a.length)];
const keverd   = a => a.map(x => [Math.random(), x]).sort((p, q) => p[0] - q[0]).map(p => p[1]);
export const NAPROV = ['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'];
export const KONYHAK = [...new Set(RECIPES.map(r => r.konyha).filter(k => k && k !== 'Általános'))].sort();

// Slot definitions — 5 meal buckets, target energy split.
const FOZOTT = ['Ebéd', 'Vacsora'];
export const SLOTS = [
  { k: 'reggeli',  cim: 'Reggeli',    a: 0.25, kat: ['Reggeli'] },
  { k: 'ebed',     cim: 'Ebéd',       a: 0.30, kat: FOZOTT },
  { k: 'vacsora',  cim: 'Vacsora',    a: 0.29, kat: FOZOTT },
  { k: 'snack',    cim: 'Kiegészítő', a: 0.09, kat: ['Tízórai', 'Uzsonna', 'Snack', 'Smoothie/ital'] },
  { k: 'desszert', cim: 'Desszert',   a: 0.07, kat: ['Desszert'] },
];

// Side-dishes that carbo-up meals when the recipe alone falls short of the target.
const KORETEK = [
  { nev: 'basmati rizs (szárazon)',   kcal: 349, p: 8,  ch: 76, zs: 1 },
  { nev: 'barna rizs (szárazon)',     kcal: 355, p: 8,  ch: 72, zs: 3 },
  { nev: 'burgonya',                  kcal: 77,  p: 2,  ch: 17, zs: 0.1 },
  { nev: 'édesburgonya',              kcal: 86,  p: 1.6, ch: 20, zs: 0.1 },
  { nev: 'teljes kiőrlésű kenyér',    kcal: 240, p: 10, ch: 41, zs: 3 },
  { nev: 'durumtészta (szárazon)',    kcal: 355, p: 13, ch: 70, zs: 1.5 },
  { nev: 'bulgur (szárazon)',         kcal: 342, p: 12, ch: 63, zs: 1.5 },
  { nev: 'banán',                     kcal: 89,  p: 1.1, ch: 23, zs: 0.3 },
];

// ============================== filters =====================================
export function isFdus(r) { return r.kcal > 0 && (r.p * 4) / r.kcal >= 0.30; }

function illik(r, diet) {
  if (diet.vegan  && !r.vegan)  return false;
  if (diet.veget  && !r.veget)  return false;
  if (diet.laktoz && r.laktoz)  return false;
  return true;
}

function konyhaOk(r, slot, konyhak) {
  if (!konyhak || !konyhak.length) return true;
  // Main meals: honour selection strictly. Others: allow "Általános" too.
  if (slot.k === 'ebed' || slot.k === 'vacsora') return konyhak.includes(r.konyha);
  return r.konyha === 'Általános' || konyhak.includes(r.konyha);
}

function feherjeOk(r, feherjedus) { return !feherjedus || isFdus(r); }

function keszlet(slot, cel, cfg) {
  // Diet is ALWAYS a hard filter; cuisine + high-protein soften if needed.
  let a = RECIPES.filter(r => r.cel === cel && slot.kat.includes(r.kat) && illik(r, cfg.diet) && konyhaOk(r, slot, cfg.konyhak) && feherjeOk(r, cfg.feherjedus));
  if (a.length < 2) a = RECIPES.filter(r => slot.kat.includes(r.kat) && illik(r, cfg.diet) && konyhaOk(r, slot, cfg.konyhak) && feherjeOk(r, cfg.feherjedus));
  if (a.length < 1) a = RECIPES.filter(r => slot.kat.includes(r.kat) && illik(r, cfg.diet) && konyhaOk(r, slot, cfg.konyhak));
  if (a.length < 1) a = RECIPES.filter(r => slot.kat.includes(r.kat) && illik(r, cfg.diet));
  return a;
}
function varakozas(slot, cel, cfg) { return clamp(keszlet(slot, cel, cfg).length - 1, 1, 3); }

// ============================== ingredient math =============================
function alapKulcs(term) {
  const t = (term || '').toLowerCase().replace(/\(.*?\)/g, '').trim();
  if (!t) return null;
  if (INGREDIENTS[t]) return t;
  let best = null;
  for (const k in INGREDIENTS) {
    const kb = k.replace(/\(.*?\)/g, '').trim();
    if (kb === t) return k;
    if (k.includes(t) || t.includes(kb)) { if (!best || k.length < best.length) best = k; }
  }
  return best;
}
function alapMakro(term) { const k = alapKulcs(term); return k ? INGREDIENTS[k] : null; }
function elsoAlap(s) { return (s || '').split(/[/·]/)[0].replace(/\(.*?\)/g, '').trim(); }

function hozzTetel(hozz, volt) {
  const vl = (volt || '').toLowerCase();
  for (const item of (hozz || '').split(',')) {
    if (!item.toLowerCase().includes(vl)) continue;
    const it = item.trim();
    const m  = it.match(/^(\d+(?:[.,]\d+)?)\s*(g|ml)\b/i);
    const nev = it.replace(/^\d+(?:[.,]\d+)?\s*(g|ml|dkg|db|ek|tk|szelet|konzerv|gerezd|csipet|fej|lap|csomag)?\s*/i, '').trim();
    return { g: m ? parseFloat(m[1].replace(',', '.')) : null, nev };
  }
  return { g: null, nev: null };
}

// Delta from ingredient swaps on the base recipe (no portion scaling).
export function makroDelta(m) {
  const d = { kcal: 0, p: 0, ch: 0, zs: 0 }; const hianyzo = [];
  if (m && m.cserek) for (const volt in m.cserek) {
    const t = hozzTetel(m.r.hozz, volt);
    const a = alapMakro(t.nev) || alapMakro(volt);
    const b = alapMakro(elsoAlap(m.cserek[volt]));
    if (t.g == null || !a || !b) { hianyzo.push(volt); continue; }
    const f = t.g / 100;
    const dk = (b.kcal - a.kcal) * f, dp = (b.p - a.p) * f, dc = (b.ch - a.ch) * f, dz = (b.zs - a.zs) * f;
    if (Math.abs(dk) > m.r.kcal * 0.7) { hianyzo.push(volt); continue; }
    d.kcal += dk; d.p += dp; d.ch += dc; d.zs += dz;
  }
  return { d, hianyzo };
}
export function emakro(m) {
  const d = makroDelta(m).d;
  return {
    kcal: Math.max(0, m.r.kcal + d.kcal), p: Math.max(0, m.r.p + d.p),
    ch:   Math.max(0, m.r.ch + d.ch),     zs: Math.max(0, m.r.zs + d.zs),
  };
}

export const osszeg = meals => meals.reduce((a, x) => {
  const k = x.koret, g = k ? k.g / 100 : 0, em = emakro(x);
  return {
    kcal: a.kcal + em.kcal * x.f + (k ? k.kcal * g : 0),
    p:    a.p    + em.p    * x.f + (k ? k.p    * g : 0),
    ch:   a.ch   + em.ch   * x.f + (k ? k.ch   * g : 0),
    zs:   a.zs   + em.zs   * x.f + (k ? k.zs   * g : 0),
  };
}, { kcal: 0, p: 0, ch: 0, zs: 0 });

// Cserepairok in the recipe's "helyett" text → [{volt, helyette}].
export function cserePairok(helyett) {
  if (!helyett) return [];
  return helyett.split('·').map(s => s.trim()).filter(Boolean).map(seg => {
    const resz = seg.split('→').map(x => x.trim());
    return { volt: resz[0] || '', helyette: resz[1] || '' };
  }).filter(p => p.volt && p.helyette);
}

// ============================== generation ==================================
function napiSulyok(edzes) {
  const t = edzes.filter(Boolean).length;
  if (t === 0 || t === 7) return edzes.map(() => 1);
  const nyers = edzes.map(x => x ? 1.06 : 0.94), ossz = nyers.reduce((a, b) => a + b, 0);
  return nyers.map(x => x * 7 / ossz);
}

function nap(i, napCel, desszertes, hasznalt, cel, cfg) {
  const slots = SLOTS.filter(s => (s.k !== 'desszert' || desszertes) && keszlet(s, cel, cfg).length > 0);
  const skala = 1 / slots.reduce((a, s) => a + s.a, 0);
  return slots.map(slot => {
    const w = varakozas(slot, cel, cfg);
    let pool = keszlet(slot, cel, cfg).filter(r => hasznalt[r.nev] === undefined || i - hasznalt[r.nev] >= w);
    if (!pool.length) pool = keszlet(slot, cel, cfg);
    const r = veletlen(pool); hasznalt[r.nev] = i;
    const cimzett = napCel * slot.a * skala;
    return { slot, r, f: clamp(Math.round(cimzett / r.kcal * 4) / 4, 0.5, 2.5), batch: false, marad: false, cserek: {}, koret: null };
  });
}

function het(t, cel, napok, cfg) {
  const sulyok = napok === 7 ? napiSulyok(cfg.edzes) : [1];
  const desszertNapok = new Set(napok === 7 ? keverd([0, 1, 2, 3, 4, 5, 6]).slice(0, 4) : [0]);
  const hasznalt = {};
  return sulyok.slice(0, napok).map((w, i) => ({
    i, edzes: napok === 7 ? cfg.edzes[i] : false, cel: t.kcal * w,
    meals: nap(i, t.kcal * w, desszertNapok.has(i), hasznalt, cel, cfg),
  }));
}

function pontszam(terv, t) {
  const napi = terv.map(d => osszeg(d.meals));
  const n = terv.length;
  const sum = k => napi.reduce((a, o) => a + o[k], 0);
  const kcalO = sum('kcal'), pO = sum('p'), chO = sum('ch'), zsO = sum('zs');
  let s = Math.abs(kcalO - t.kcal * n) / (t.kcal * n) * 100 * 3;
  s += Math.abs(pO - t.feherje * n) / (t.feherje * n) * 100 * 1.2;
  s += Math.max(0, (t.feherje * n - pO) / (t.feherje * n)) * 100 * 1.2;
  s += Math.abs(chO - t.szenhidrat * n) / (t.szenhidrat * n) * 100 * 0.7;
  s += Math.abs(zsO - t.zsir * n) / (t.zsir * n) * 100 * 0.7;
  terv.forEach((d, i) => { const e = Math.abs(napi[i].kcal - d.cel) / d.cel * 100; s += Math.max(0, e - 8); });
  const db = {}; terv.forEach(d => d.meals.forEach(m => db[m.r.nev] = (db[m.r.nev] || 0) + 1));
  s += Object.values(db).filter(v => v > 2).length * 6;
  return s;
}

function elofozes(terv) {
  if (terv.length < 2) return;
  let parok = 0;
  for (const i of keverd([0, 1, 2, 3, 4, 5])) {
    if (parok >= 2 || i + 1 >= terv.length) continue;
    const e = terv[i].meals.find(m => m.slot.k === 'ebed');
    const v = terv[i + 1].meals.find(m => m.slot.k === 'vacsora');
    if (!e || !v || e.batch || v.marad || e.r.nev === v.r.nev) continue;
    e.batch = true; v.r = e.r; v.marad = true;
    v.f = clamp(Math.round(terv[i + 1].cel * v.slot.a / v.r.kcal * 4) / 4, 0.5, 2.5);
    parok++;
  }
}

function koretezes(terv, t) {
  const n = terv.length;
  const hiany = t.szenhidrat * n - terv.reduce((a, d) => a + osszeg(d.meals).ch, 0);
  if (hiany <= n * 8) return;
  const naponta = hiany / n;
  terv.forEach(d => {
    const jeloltek = d.meals.filter(m => (m.slot.k === 'ebed' || m.slot.k === 'vacsora') && !m.marad);
    if (!jeloltek.length) return;
    const db = Math.min(2, jeloltek.length), resz = naponta / db;
    keverd(jeloltek).slice(0, db).forEach(m => {
      const k = veletlen(KORETEK);
      m.koret = { ...k, g: clamp(Math.round(resz / k.ch * 100 / 10) * 10, 20, 260) };
    });
  });
}

function csiszol(terv, t) {
  let best = pontszam(terv, t);
  for (let i = 0; i < 600; i++) {
    const d = veletlen(terv), m = veletlen(d.meals);
    const koretet = m.koret && Math.random() < 0.5;
    const regi = koretet ? m.koret.g : m.f;
    if (koretet) m.koret.g = clamp(regi + (Math.random() < 0.5 ? 10 : -10), 20, 300);
    else m.f = clamp(regi + (Math.random() < 0.5 ? 0.25 : -0.25), 0.5, 2.5);
    const uj = pontszam(terv, t);
    if (uj < best) best = uj; else if (koretet) m.koret.g = regi; else m.f = regi;
  }
}

/**
 * Generate a plan.
 *   target: { kcal, feherje, zsir, szenhidrat }
 *   goal:   'Fogyás' | 'Szintentartás' | 'Izomépítés'
 *   napok:  1 | 7
 *   cfg:    { diet:{veget,vegan,laktoz}, konyhak:[], feherjedus:bool, edzes:[7bools] }
 */
export function generalj(target, goal, napok, cfg) {
  let best = null, bp = 1e9;
  for (let i = 0; i < (napok === 7 ? 60 : 300); i++) {
    const c = het(target, goal, napok, cfg), p = pontszam(c, target);
    if (p < bp) { bp = p; best = c; }
  }
  if (napok === 7) elofozes(best);
  koretezes(best, target);
  csiszol(best, target);
  return best;
}

/** 4 weeks (28 days) laid flat with `.het` (0-3) tagged. */
export function generalHonap(target, goal, cfg) {
  const out = [];
  for (let w = 0; w < 4; w++) {
    const het7 = generalj(target, goal, 7, cfg);
    het7.forEach(d => { d.het = w; });
    out.push(...het7);
  }
  return out;
}

// ============================== swaps (portal) ==============================
export function slotCel(d, slot) {
  const aktivA = d.meals.reduce((a, x) => a + x.slot.a, 0);
  return d.cel * (slot.a / aktivA);
}

/** Replace an entire meal at [dayIndex][mealIndex] with a random calorie-fair swap. Mutates `terv`. */
export function cserelFogas(terv, di, mi, cel, cfg) {
  const d = terv[di], m = d.meals[mi];
  const napNevek = new Set(d.meals.map(x => x.r.nev));
  let pool = keszlet(m.slot, cel, cfg).filter(r => r.nev !== m.r.nev && !napNevek.has(r.nev));
  if (!pool.length) pool = keszlet(m.slot, cel, cfg).filter(r => r.nev !== m.r.nev);
  if (!pool.length) return terv;
  const r = veletlen(pool);
  m.r = r; m.cserek = {}; m.koret = null; m.batch = false; m.marad = false;
  const cimzett = slotCel(d, m.slot);
  m.f = clamp(Math.round(cimzett / r.kcal * 4) / 4, 0.5, 2.5);
  return terv;
}

/** Toggle an ingredient swap defined by the recipe's `helyett` field. Mutates `terv`. */
export function cserelHozzavalo(terv, di, mi, volt) {
  const m = terv[di].meals[mi];
  const p = cserePairok(m.r.helyett).find(x => x.volt === volt);
  if (!p) return terv;
  m.cserek = m.cserek || {};
  if (m.cserek[volt]) delete m.cserek[volt]; else m.cserek[volt] = p.helyette;
  return terv;
}

// ============================== rendering helpers ===========================
export function skalaz(szoveg, f) {
  return szoveg.split(',').map(x => x.trim()).filter(Boolean).map(item => {
    const m = item.match(/^(\d+(?:[.,]\d+)?)\s*(g|ml|dkg|db|ek|tk|szelet|konzerv)?\b\s*(.*)$/i);
    if (!m) return { qty: null, unit: '', nev: item };
    let q = parseFloat(m[1].replace(',', '.')) * f;
    const unit = (m[2] || '').toLowerCase();
    q = (unit === 'g' || unit === 'ml') ? Math.max(5, Math.round(q / 5) * 5) : Math.round(q * 2) / 2;
    return { qty: q, unit, nev: m[3] || '' };
  });
}

function csereSzoveg(str, keres, csere) {
  const i = str.toLowerCase().indexOf(keres.toLowerCase());
  if (i < 0) return str;
  return str.slice(0, i) + csere + str.slice(i + keres.length);
}

/** Applied ingredient list for a meal after its per-portion scaling + swaps. */
export function alkalmazottHozz(m) {
  let lista = skalaz(m.r.hozz, m.f);
  if (m.cserek) {
    for (const volt in m.cserek) {
      const helyette = m.cserek[volt], vl = volt.toLowerCase();
      lista = lista.map(x => (x.nev && x.nev.toLowerCase().includes(vl))
        ? { ...x, nev: csereSzoveg(x.nev, volt, helyette), csereltE: true } : x);
    }
  }
  return lista;
}

// ============================== bevásárlólista ==============================
const KATEGORIAK = [
  ['Hús, hal, tojás', ['csirke', 'pulyka', 'marha', 'sertés', 'darált', 'sonka', 'bacon', 'kolbász', 'virsli', 'szalámi', 'hal', 'lazac', 'tonhal', 'tőkehal', 'garnéla', 'tenger', 'tojás', 'máj']],
  ['Tejtermék', ['túró', 'joghurt', 'skyr', 'kefir', 'tej', 'sajt', 'feta', 'mozzarella', 'parmezán', 'provolone', 'cottage', 'tejföl', 'tejszín', 'vaj', 'körözhető', 'ricotta', 'mascarpone']],
  ['Zöldség, gyümölcs', ['paradicsom', 'paprika', 'uborka', 'hagyma', 'fokhagyma', 'saláta', 'spenót', 'brokkoli', 'karfiol', 'cukkini', 'padlizsán', 'sárgarépa', 'répa', 'cékla', 'burgonya', 'krumpli', 'gomba', 'kukorica', 'borsó', 'zöldbab', 'káposzta', 'banán', 'alma', 'eper', 'málna', 'áfonya', 'szőlő', 'füge', 'narancs', 'citrom', 'lime', 'körte', 'barack', 'datolya', 'meggy', 'ananász', 'mangó', 'avokádó', 'olajbogyó', 'zeller', 'retek', 'jégsaláta', 'rukkola', 'lilahagyma', 'újhagyma', 'petrezselyem', 'bazsalikom', 'koriander', 'kapor']],
  ['Gabona, pékáru', ['zab', 'rizs', 'tészta', 'bulgur', 'kuszkusz', 'quinoa', 'hajdina', 'kenyér', 'zsemle', 'kifli', 'tortilla', 'pita', 'liszt', 'puffasztott', 'müzli', 'granola', 'gnocchi', 'piskóta', 'korpa', 'árpa', 'basmati', 'jázmin']],
  ['Olaj, mag, egyéb', ['olaj', 'olíva', 'mandula', 'dió', 'mogyoró', 'kesu', 'pisztácia', 'chia', 'lenmag', 'napraforgó', 'tökmag', 'szezám', 'tahini', 'mogyorókrém', 'mandulakrém', 'kókusz', 'méz', 'eritrit', 'xilit', 'szirup', 'kakaó', 'csokoládé', 'protein', 'fehérjepor', 'ecet', 'alaplé', 'leveskocka', 'majonéz', 'mustár', 'ketchup', 'szósz', 'konzerv', 'bab', 'lencse', 'csicseriborsó', 'tofu', 'tempeh', 'szója']],
];

function kategoria(nev) {
  const n = (nev || '').toLowerCase();
  for (const [cim, kulcsok] of KATEGORIAK) {
    if (kulcsok.some((k) => n.includes(k))) return cim;
  }
  return 'Fűszerek, egyéb';
}

/**
 * Összesített bevásárlólista a teljes tervre (akár 7 napra). Minden étkezés
 * hozzávalóját összeadja — a maradékként (marad) megjelölt adagokat is, hiszen
 * azok is elfogynak, csak előző nap készülnek el. Kategóriák szerint csoportosít.
 */
/**
 * Összesített bevásárlólista. Ha a terv több hétre szól (a napok `het`
 * indexe alapján), hetekre bontva is visszaadja — plusz a teljes időszakra
 * vonatkozó összesítést.
 */
export function bevasarloLista(terv) {
  const napok = terv || [];
  const hetek = [...new Set(napok.map((d) => d.het ?? 0))].sort((a, b) => a - b);
  const egy = bevasarloEgyLista(napok);
  if (hetek.length < 2) {
    return { ...egy, weeks: [], multiWeek: false };
  }
  return {
    ...egy,
    multiWeek: true,
    weeks: hetek.map((h) => ({
      het: h,
      cim: `${h + 1}. hét`,
      ...bevasarloEgyLista(napok.filter((d) => (d.het ?? 0) === h)),
    })),
  };
}

function bevasarloEgyLista(napok) {
  const acc = new Map();   // kulcs: normalizált név → { nev, units: Map(unit→qty), becsult }
  const add = (nev, qty, unit) => {
    const name = (nev || '').trim();
    if (!name) return;
    const key = name.toLowerCase();
    let it = acc.get(key);
    if (!it) {
      it = { nev: name, units: new Map(), becsult: false };
      acc.set(key, it);
    }
    if (qty == null) {
      it.becsult = true;
      return;
    }
    const u = unit || 'db';
    it.units.set(u, (it.units.get(u) || 0) + qty);
  };

  (napok || []).forEach((d) => {
    (d.meals || []).forEach((m) => {
      alkalmazottHozz(m).forEach((x) => add(x.nev, x.qty, x.unit));
      if (m.koret) add(m.koret.nev, m.koret.g, 'g');
    });
  });

  const items = [...acc.values()].map((x) => {
    const parts = [...x.units.entries()].map(([unit, qty]) => ({
      unit,
      qty: (unit === 'g' || unit === 'ml') ? Math.round(qty / 5) * 5 : Math.round(qty * 2) / 2,
    }));
    const label = parts.length
      ? parts.map((p) => `${p.qty} ${p.unit}`).join(' + ') + (x.becsult ? ' + ízlés szerint' : '')
      : 'ízlés szerint';
    return { nev: x.nev, parts, label, kat: kategoria(x.nev) };
  });

  const sorrend = [...KATEGORIAK.map(([c]) => c), 'Fűszerek, egyéb'];
  const groups = sorrend
    .map((cim) => ({
      cim,
      items: items.filter((i) => i.kat === cim).sort((a, b) => a.nev.localeCompare(b.nev, 'hu')),
    }))
    .filter((g) => g.items.length > 0);

  return { groups, total: items.length, napok: (napok || []).length };
}

export function bevasarloSor(i) {
  return `${i.nev} — ${i.label}`;
}

// ==================== Persistable ↔ runtime plan converters =================
/**
 * The runtime "terv" carries the full recipe object on each meal (`m.r`).
 * For persistence we serialize to the schema in Fejlesztoi_atado_etrendgenerator.md:
 *   { slot, recept_id, recept_snapshot, f, koret, batch, marad, cserek }
 * `recept_id` = slug(recept.nev) — stable across renames.
 */
export function slug(s) {
  return (s || '').toLowerCase()
    .replace(/[áàâã]/g, 'a').replace(/[éèêë]/g, 'e').replace(/[íìîï]/g, 'i')
    .replace(/[óòôõö]/g, 'o').replace(/[őú]/g, 'o').replace(/[úùûüű]/g, 'u')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

export function toPersistable(terv) {
  return terv.map(d => ({
    het: d.het ?? 0,
    nap: d.i ?? 0,
    edzes: !!d.edzes,
    cel_kcal: Math.round(d.cel),
    meals: d.meals.map(m => ({
      slot: m.slot.k,
      recept_id: slug(m.r.nev),
      recept_snapshot: { ...m.r },
      f: m.f,
      koret: m.koret,
      batch: !!m.batch,
      marad: !!m.marad,
      cserek: { ...(m.cserek || {}) },
    })),
  }));
}

export function fromPersistable(plan) {
  return plan.map(d => ({
    i: d.nap ?? 0,
    het: d.het ?? 0,
    edzes: !!d.edzes,
    cel: d.cel_kcal ?? 0,
    meals: (d.meals || []).map(pm => {
      const slotDef = SLOTS.find(s => s.k === pm.slot) || SLOTS[0];
      return {
        slot: slotDef,
        r: pm.recept_snapshot || {},
        f: pm.f ?? 1,
        koret: pm.koret || null,
        batch: !!pm.batch,
        marad: !!pm.marad,
        cserek: { ...(pm.cserek || {}) },
      };
    }),
  }));
}
